/**
 * FUNCIONES DE MATCHING Y VINCULACIÓN
 * Proyecto: RESIDO - AMPI Riviera Nayarit
 * Fecha: 2025-10-18
 *
 * Algoritmos para vincular datos de diferentes fuentes
 */

import { MatchResult } from '../types/entities';

// ============================================================================
// NORMALIZACIÓN DE STRINGS
// ============================================================================

/**
 * Normaliza un string para comparación
 * - Convierte a minúsculas
 * - Elimina espacios extras
 * - Elimina acentos
 * - Elimina caracteres especiales
 */
export function normalize(str: string | null | undefined): string {
  if (!str) return '';

  return str
    .toLowerCase()
    .trim()
    .normalize('NFD')                          // Descomponer caracteres con acentos
    .replace(/[\u0300-\u036f]/g, '')           // Eliminar marcas diacríticas
    .replace(/[^a-z0-9\s]/g, '')               // Solo letras, números y espacios
    .replace(/\s+/g, ' ');                     // Normalizar espacios
}

/**
 * Elimina palabras comunes que no aportan al matching
 */
export function removeCommonWords(str: string): string {
  const commonWords = [
    'real', 'estate', 'bienes', 'raices', 'inmobiliaria', 'inmobiliarias',
    'agencia', 'agencias', 'grupo', 'properties', 'property', 'realty',
    'mexico', 'sa', 'de', 'cv', 'sc', 'srl', 'the', 'and', 'y', 'e'
  ];

  let normalized = normalize(str);

  commonWords.forEach(word => {
    const regex = new RegExp(`\\b${word}\\b`, 'g');
    normalized = normalized.replace(regex, '');
  });

  return normalized.replace(/\s+/g, ' ').trim();
}

// ============================================================================
// LEVENSHTEIN DISTANCE
// ============================================================================

/**
 * Calcula la distancia de Levenshtein entre dos strings
 * (número de ediciones necesarias para transformar un string en otro)
 */
export function levenshteinDistance(str1: string, str2: string): number {
  const s1 = normalize(str1);
  const s2 = normalize(str2);

  if (s1 === s2) return 0;
  if (s1.length === 0) return s2.length;
  if (s2.length === 0) return s1.length;

  const matrix: number[][] = [];

  // Inicializar primera columna y fila
  for (let i = 0; i <= s2.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= s1.length; j++) {
    matrix[0][j] = j;
  }

  // Llenar matriz
  for (let i = 1; i <= s2.length; i++) {
    for (let j = 1; j <= s1.length; j++) {
      if (s2.charAt(i - 1) === s1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // Substitución
          matrix[i][j - 1] + 1,     // Inserción
          matrix[i - 1][j] + 1      // Eliminación
        );
      }
    }
  }

  return matrix[s2.length][s1.length];
}

/**
 * Calcula similaridad basada en Levenshtein (0-100)
 */
export function calculateSimilarity(str1: string, str2: string): number {
  const distance = levenshteinDistance(str1, str2);
  const maxLength = Math.max(str1.length, str2.length);

  if (maxLength === 0) return 100;

  return Math.round(((maxLength - distance) / maxLength) * 100);
}

// ============================================================================
// MATCHING DE AGENCIAS
// ============================================================================

export interface AgenciaItem {
  inmobiliaria_id: string;
  nombre: string;
}

/**
 * Encuentra la agencia que mejor hace match con el nombre dado
 */
export function matchAgencia(
  nombreBuscado: string,
  agencias: AgenciaItem[]
): MatchResult {
  if (!nombreBuscado || !agencias || agencias.length === 0) {
    return {
      found: false,
      confidence: 0,
      method: 'none',
      original: nombreBuscado
    };
  }

  const normalizedBuscado = normalize(nombreBuscado);
  const cleanedBuscado = removeCommonWords(nombreBuscado);

  // 1. MATCH EXACTO (normalizado)
  for (const agencia of agencias) {
    if (normalize(agencia.nombre) === normalizedBuscado) {
      return {
        found: true,
        id: agencia.inmobiliaria_id,
        confidence: 100,
        method: 'exact',
        original: nombreBuscado,
        matched: agencia.nombre
      };
    }
  }

  // 2. MATCH PARCIAL (contiene)
  for (const agencia of agencias) {
    const normalizedAgencia = normalize(agencia.nombre);

    // Buscar si uno contiene al otro
    if (normalizedAgencia.includes(normalizedBuscado) ||
        normalizedBuscado.includes(normalizedAgencia)) {
      return {
        found: true,
        id: agencia.inmobiliaria_id,
        confidence: 85,
        method: 'partial',
        original: nombreBuscado,
        matched: agencia.nombre
      };
    }

    // Buscar sin palabras comunes
    const cleanedAgencia = removeCommonWords(agencia.nombre);
    if (cleanedAgencia && cleanedBuscado &&
        (cleanedAgencia.includes(cleanedBuscado) ||
         cleanedBuscado.includes(cleanedAgencia))) {
      return {
        found: true,
        id: agencia.inmobiliaria_id,
        confidence: 80,
        method: 'partial',
        original: nombreBuscado,
        matched: agencia.nombre
      };
    }
  }

  // 3. MATCH FUZZY (Levenshtein)
  let bestMatch: AgenciaItem | null = null;
  let bestSimilarity = 0;

  for (const agencia of agencias) {
    const similarity = calculateSimilarity(nombreBuscado, agencia.nombre);

    if (similarity > bestSimilarity) {
      bestSimilarity = similarity;
      bestMatch = agencia;
    }
  }

  // Aceptar fuzzy match si similaridad > 70%
  if (bestMatch && bestSimilarity >= 70) {
    return {
      found: true,
      id: bestMatch.inmobiliaria_id,
      confidence: bestSimilarity,
      method: 'fuzzy',
      original: nombreBuscado,
      matched: bestMatch.nombre
    };
  }

  // 4. NO MATCH
  return {
    found: false,
    confidence: bestSimilarity,
    method: 'none',
    original: nombreBuscado,
    matched: bestMatch?.nombre
  };
}

// ============================================================================
// MATCHING DE ASESORES
// ============================================================================

export interface AsesorItem {
  asesor_id?: string;
  asociado_id?: string;
  nombre: string;
  nombre_flex?: string;
  correo?: string;
  correo_flex?: string;
}

/**
 * Encuentra el asesor/asociado que mejor hace match con el nombre dado
 */
export function matchAsesor(
  nombreBuscado: string,
  asesores: AsesorItem[]
): MatchResult {
  if (!nombreBuscado || !asesores || asesores.length === 0) {
    return {
      found: false,
      confidence: 0,
      method: 'none',
      original: nombreBuscado
    };
  }

  const normalizedBuscado = normalize(nombreBuscado);

  // 1. MATCH EXACTO por nombre_flex
  for (const asesor of asesores) {
    if (asesor.nombre_flex && normalize(asesor.nombre_flex) === normalizedBuscado) {
      return {
        found: true,
        id: asesor.asesor_id || asesor.asociado_id,
        confidence: 100,
        method: 'exact',
        original: nombreBuscado,
        matched: asesor.nombre
      };
    }
  }

  // 2. MATCH EXACTO por nombre
  for (const asesor of asesores) {
    if (normalize(asesor.nombre) === normalizedBuscado) {
      return {
        found: true,
        id: asesor.asesor_id || asesor.asociado_id,
        confidence: 100,
        method: 'exact',
        original: nombreBuscado,
        matched: asesor.nombre
      };
    }
  }

  // 3. MATCH PARCIAL (primer nombre + apellido)
  const partesBuscado = normalizedBuscado.split(' ').filter(p => p.length > 0);

  for (const asesor of asesores) {
    const partesAsesor = normalize(asesor.nombre).split(' ').filter(p => p.length > 0);

    // Si primer nombre Y último apellido coinciden
    if (partesBuscado.length >= 2 && partesAsesor.length >= 2) {
      const primerNombreBuscado = partesBuscado[0];
      const apellidoBuscado = partesBuscado[partesBuscado.length - 1];
      const primerNombreAsesor = partesAsesor[0];
      const apellidoAsesor = partesAsesor[partesAsesor.length - 1];

      if (primerNombreBuscado === primerNombreAsesor &&
          apellidoBuscado === apellidoAsesor) {
        return {
          found: true,
          id: asesor.asesor_id || asesor.asociado_id,
          confidence: 85,
          method: 'partial',
          original: nombreBuscado,
          matched: asesor.nombre
        };
      }
    }
  }

  // 4. MATCH FUZZY
  let bestMatch: AsesorItem | null = null;
  let bestSimilarity = 0;

  for (const asesor of asesores) {
    // Comparar con nombre principal
    let similarity = calculateSimilarity(nombreBuscado, asesor.nombre);

    // También comparar con nombre_flex si existe
    if (asesor.nombre_flex) {
      const flexSimilarity = calculateSimilarity(nombreBuscado, asesor.nombre_flex);
      similarity = Math.max(similarity, flexSimilarity);
    }

    if (similarity > bestSimilarity) {
      bestSimilarity = similarity;
      bestMatch = asesor;
    }
  }

  // Aceptar fuzzy match si similaridad > 75%
  if (bestMatch && bestSimilarity >= 75) {
    return {
      found: true,
      id: bestMatch.asesor_id || bestMatch.asociado_id,
      confidence: bestSimilarity,
      method: 'fuzzy',
      original: nombreBuscado,
      matched: bestMatch.nombre
    };
  }

  // 5. NO MATCH
  return {
    found: false,
    confidence: bestSimilarity,
    method: 'none',
    original: nombreBuscado,
    matched: bestMatch?.nombre
  };
}

// ============================================================================
// ANÁLISIS DE MATCHES
// ============================================================================

/**
 * Analiza qué tan bueno es un match
 */
export function evaluateMatch(match: MatchResult): {
  status: 'excellent' | 'good' | 'uncertain' | 'poor';
  requiresReview: boolean;
} {
  if (!match.found) {
    return { status: 'poor', requiresReview: true };
  }

  if (match.method === 'exact') {
    return { status: 'excellent', requiresReview: false };
  }

  if (match.method === 'partial' && match.confidence >= 85) {
    return { status: 'excellent', requiresReview: false };
  }

  if (match.confidence >= 90) {
    return { status: 'excellent', requiresReview: false };
  }

  if (match.confidence >= 80) {
    return { status: 'good', requiresReview: false };
  }

  if (match.confidence >= 70) {
    return { status: 'uncertain', requiresReview: true };
  }

  return { status: 'poor', requiresReview: true };
}

/**
 * Genera un reporte legible de un match
 */
export function formatMatchReport(match: MatchResult): string {
  if (!match.found) {
    return `❌ NO MATCH: "${match.original}" (mejor candidato: ${match.matched || 'ninguno'}, ${match.confidence}%)`;
  }

  const { status } = evaluateMatch(match);

  const emoji = status === 'excellent' ? '✅' :
                status === 'good' ? '✔️' :
                status === 'uncertain' ? '⚠️' : '❓';

  return `${emoji} ${status.toUpperCase()}: "${match.original}" → "${match.matched}" (${match.method}, ${match.confidence}%)`;
}
